// src/hooks/useJanetMemory.js — Supabase-backed persistence for Janet chat turns,
// scoped to ONE topic (janet_chat_sessions.id === janet_chat_messages.session_id).
//
// Public API:
//   dbMessages   — [{role, content, actionType?, pageContext?, ...cardPayloads}] ASC for `sessionId`
//   loading      — true while the topic's turns are loading
//   saveMessage  — async (role, content, {pageContext?, actionType?, payload?}) → { ok, error? }
//                  also touches the session row (title / last_message_at / count).
//                  Still never THROWS (every existing fire-and-forget caller
//                  that ignores the return value keeps working exactly as
//                  before) — but a genuine write failure is no longer
//                  INVISIBLE: it used to resolve the same way as a real
//                  success (`undefined`, either way), so a caller that
//                  actually needs to know whether an instruction was durably
//                  recorded before proceeding (review round 4, finding #4:
//                  "useJanetMemory.saveMessage swallows errors") had no way
//                  to tell. `{ ok: false, error }` on failure, `{ ok: true }`
//                  on success — check it when the caller's next step depends
//                  on the write having actually landed.
//
// Card payloads (draftPayload, taskPayload, calendarPayload, callPayload,
// scheduleProposalPayload, schedulingPayload, queuedDraft) ride in the
// `payload` JSONB column so a resumed topic keeps its actionable cards. If the
// column isn't there yet (migration pending) the insert retries without it.

import { useState, useEffect, useCallback } from 'react'
import supabase from '../lib/supabase'
import { queuedWrite } from '../lib/queuedWrite'
import { touchSession } from './useJanetSessions'
import { CARD_KEYS, pickCardPayload } from '../lib/janetTopics'
export { CARD_KEYS, pickCardPayload }

const LOAD_LIMIT = 200
const MAX_CONTENT = 8000  // chars — trim very long assistant responses

const EMPTY = []
let payloadColumnKnownMissing = false

export function useJanetMemory(userId, sessionId) {
  // { forSession, rows } — derived per active topic so switching topics never
  // shows the previous topic's rows and needs no synchronous reset.
  const [loaded, setLoadedState] = useState({ forSession: null, rows: [] })
  const dbMessages = loaded.forSession === sessionId && loaded.owner === userId ? loaded.rows : EMPTY
  const loading = !!userId && !!sessionId && (loaded.forSession !== sessionId || loaded.owner !== userId)

  useEffect(() => {
    if (!userId || !sessionId) return
    let cancelled = false
    const setLoaded = next => setLoadedState({ ...next, owner: userId })
    const cols = payloadColumnKnownMissing
      ? 'role, content, action_type, page_context, session_id, created_at'
      : 'role, content, action_type, page_context, session_id, created_at, payload'
    supabase
      .from('janet_chat_messages')
      .select(cols)
      .eq('user_id', userId)
      .eq('session_id', sessionId)
      .order('created_at', { ascending: false })
      .limit(LOAD_LIMIT)
      .then(async ({ data, error }) => {
        if (error && !payloadColumnKnownMissing && /payload/i.test(error.message || '')) {
          payloadColumnKnownMissing = true
          const retry = await supabase
            .from('janet_chat_messages')
            .select('role, content, action_type, page_context, session_id, created_at')
            .eq('user_id', userId).eq('session_id', sessionId)
            .order('created_at', { ascending: false }).limit(LOAD_LIMIT)
          data = retry.data; error = retry.error
        }
        if (cancelled) return
        if (error || !data) { setLoaded({ forSession: sessionId, rows: [] }); return }
        setLoaded({ forSession: sessionId, rows: [...data].reverse().map(r => ({
          role:        r.role,
          content:     r.content,
          actionType:  r.action_type  || undefined,
          pageContext: r.page_context || undefined,
          sessionId:   r.session_id,
          createdAt:   r.created_at,
          ...(r.payload && typeof r.payload === 'object' ? r.payload : {}),
        })) })
      })
    return () => { cancelled = true }
  }, [userId, sessionId])

  const saveMessage = useCallback(async (role, content, opts = {}) => {
    if (!userId || !sessionId || !content) return { ok: false, error: 'No active topic to save into' }
    const text = typeof content === 'string' ? content : String(content)
    const row = {
      user_id:      userId,
      session_id:   sessionId,
      role,
      content:      text.slice(0, MAX_CONTENT),
      page_context: opts.pageContext  || null,
      action_type:  opts.actionType   || null,
    }
    if (opts.payload && !payloadColumnKnownMissing) row.payload = opts.payload
    try {
      await queuedWrite('janet_chat_messages', 'insert', row)
    } catch (e) {
      if (row.payload && /payload/i.test(String(e?.message || ''))) {
        payloadColumnKnownMissing = true
        delete row.payload
        try { await queuedWrite('janet_chat_messages', 'insert', row) }
        catch (e2) { return { ok: false, error: e2?.message || String(e2) } }
      } else {
        return { ok: false, error: e?.message || String(e) }
      }
    }
    touchSession({ userId, sessionId, role, content: text, page: opts.pageContext }).catch(() => {})
    return { ok: true }
  }, [userId, sessionId])

  return { dbMessages, saveMessage, loading }
}
